import React, { useEffect, useState } from 'react'
import '@typeform/embed/build/css/popup.css'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import Image from 'next/legacy/image'
import { GetVenueDetailQuery, Locale } from 'generated/graphcms'

import Anchor from '@/components/ui/Anchor/Anchor'
import Spinner from '@/components/ui/Spinner/Spinner'
import VenueFormFirstStep from '@/components/shared/VenueForm/VenueFormFirstStep'
import ListItemIcon from 'components/ui/List/ListItemIcon'
import Mapbox from 'components/ui/Mapbox/Mapbox'
import CardFacilities from 'components/ui/Card/CardFacilities'
import CardActivity from 'components/ui/Card/CardActivity'
import Accordion from 'components/ui/Accordion/Accordion'
import IconCalendar from 'components/ui/Icon/IconCalendar'
import IconUsers from 'components/ui/Icon/IconUsers'
import IconHome from 'components/ui/Icon/IconHome'
import IconFrameBounds from 'components/ui/Icon/IconFrameBounds'
import IconEarthGreen from 'components/ui/Icon/IconEarthGreen'
import IconCheckmarkCircle from 'components/ui/Icon/IconCheckmarkCircle'
import IconEuroCircle from 'components/ui/Icon/IconEuroCircle'
import IconAlarm from 'components/ui/Icon/IconAlarm'
import PriceList from 'components/ui/PriceList/PriceList'
import ModalGallery from 'components/ui/Modal/ModalGallery'

import { CachedRequest } from '../../../hooks/useSubmitVenueRequest'
import getPreviousCachedRequest from '../../../utils/getPreviousCachedRequest'
import formatDateRange from '../../../utils/formatDateRange'
import formatPrice from '../../../utils/formatPrice'
import { APP_ROUTES } from '../../../utils/constants'

export interface Props {
  locale: Locale
  venue: GetVenueDetailQuery['venue']
}

const VenueDetails: React.FC<Props> = ({ locale, venue }) => {
  const { t } = useTranslation()

  const [prevRequests, setPrevRequests] = useState<CachedRequest[]>([])

  useEffect(() => {
    if (venue?.slug) {
      setPrevRequests(getPreviousCachedRequest(venue.slug))
    }
  }, [venue?.slug])

  const [openGallery, setOpenGallery] = React.useState(false)
  const closeModal = () => {
    setOpenGallery(false)
  }

  const openModal = () => {
    setOpenGallery(true)
  }

  if (!venue) {
    return <Spinner />
  }

  const bathroomsTotal = (venue.bathroomsPrivateLimit || 0) + (venue.bathroomsShared || 0)
  const roomDetails = [
    venue.singleRooms ? t('venue.details.rooms.singleRooms', { count: venue.singleRooms }) : '',
    venue.doubleRooms ? t('venue.details.rooms.doubleRooms', { count: venue.doubleRooms }) : '',
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <>
      {prevRequests.length > 0 && (
        <div className="col-span-4 mt-4 flex flex-col items-center space-y-2 rounded bg-green-600 bg-opacity-10 px-4 py-2 text-sm font-medium md:flex-row md:space-x-3 md:space-y-0">
          <div className="flex items-center gap-2">
            <IconCalendar size="24" />
            <p>{t('venue.requestForm.sameRequestError')}:</p>
          </div>
          <div className="flex cursor-pointer flex-wrap space-x-2 text-left md:flex-nowrap">
            {prevRequests.map((request) => (
              <Link
                href={`${APP_ROUTES.OFFER}/${venue?.slug}?request=${request.requestUuid}&event=${request.eventUuid}`}
                passHref
                legacyBehavior
                key={request.requestUuid}
              >
                <Anchor className="flex items-center space-x-1" target="_blank">
                  <span>|</span>
                  <span>
                    {formatDateRange(
                      new Date(request.dateArrival),
                      new Date(request.dateDeparture),
                      locale
                    )}
                  </span>
                </Anchor>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 grid grid-cols-3 gap-3 text-sm md:text-base lg:mt-10 lg:flex lg:items-center lg:gap-0 lg:space-x-6 lg:text-lg">
        {venue.venueType && (
          <div className="flex items-center space-x-2">
            <IconHome size="24" />
            <span className="leading-none">{venue.venueType.title}</span>
          </div>
        )}
        {venue.groupSize && (
          <div className="flex items-center space-x-2">
            <IconUsers size="24" className="flex-shrink-0" />
            <span className="leading-none">
              {t('venue.groupSize.count', { count: venue.groupSize })}
            </span>
          </div>
        )}
        {!!venue.roomsTotal && (
          <div className="flex items-center space-x-2 md:col-auto">
            <IconFrameBounds size="24" className="flex-shrink-0" />
            <span className="leading-none">
              {t('venue.rooms.count', { count: venue.roomsTotal })}
            </span>
          </div>
        )}
        {venue.ecoFriendly && (
          <div className="flex items-center space-x-2">
            <IconEarthGreen fillNone size="24" className="flex-shrink-0" />
            <span className="leading-none">{t('venue.ecoFriendly')}</span>
          </div>
        )}
        {(!!venue.minPrice || !!venue.minPriceForEntireVenue) && (
          <div className="flex items-center space-x-2">
            <IconEuroCircle fillNone size="24" className="flex-shrink-0" />
            <span className="leading-none">
              {venue.minPriceForEntireVenue
                ? t('venue.details.minPriceForEntireVenue.price', {
                    price: formatPrice(venue.minPriceForEntireVenue, 0),
                  })
                : !!venue.minPrice &&
                  t('venue.minPricePerNight.price', { price: formatPrice(venue.minPrice, 0) })}
            </span>
          </div>
        )}
      </div>

      <div className="mt-3 grid grid-cols-6 grid-rows-2 gap-2 md:mt-5 lg:gap-5">
        <div className="col-span-4 row-span-2">
          {venue.gallery.length > 0 && (
            <button type="button" className="h-full w-full" onClick={openModal}>
              <Image
                src={venue.gallery[0].url1560_1000}
                width={780}
                height={500}
                layout="responsive"
                alt={venue.title}
                className="rounded-md"
              />
            </button>
          )}
        </div>
        <div className="col-span-2">
          {venue.gallery.length > 1 && (
            <button type="button" className="h-full w-full" onClick={openModal}>
              <Image
                src={venue.gallery[1].url760_480}
                width={380}
                height={240}
                layout="responsive"
                alt={venue.title}
                className="rounded-md"
              />
            </button>
          )}
        </div>
        <div className="col-span-2">
          {venue.gallery.length > 2 && (
            <button type="button" className="relative h-full w-full" onClick={openModal}>
              {venue.gallery.length > 3 && (
                <div className="absolute top-0 z-10 flex h-full w-full items-center justify-center rounded-md bg-white bg-opacity-75">
                  <span className="font-semibold md:text-3xl">
                    {t('venue.photoGalleryButton', { count: venue.gallery.length - 2 })}
                  </span>
                </div>
              )}
              <Image
                src={venue.gallery[2].url760_480}
                width={380}
                height={240}
                layout="responsive"
                alt={venue.title}
                className="rounded-md"
              />
            </button>
          )}
        </div>
      </div>

      <div className="mt-2 flex flex-col-reverse space-y-2 space-y-reverse md:grid md:grid-cols-6 md:gap-2 md:space-y-0 lg:mt-5 lg:gap-5">
        {venue.alert && (
          <div className="col-span-4 flex items-center space-x-3 rounded bg-primary-600 bg-opacity-20 px-4 py-2 text-sm font-medium md:py-2">
            <div className="flex-shrink-0 self-start">
              <IconAlarm size="24" />
            </div>
            <p>{venue.alert}</p>
          </div>
        )}
      </div>

      <div className="mt-10 grid grid-cols-1 gap-6 md:mt-20 md:grid-cols-2 md:gap-16">
        {venue.productivityHighlights.length > 0 && (
          <div className="space-y-2 md:flex md:flex-col md:space-y-5">
            <p className="font-semibold md:text-xl">{t('venue.highlights')}</p>

            <div className="flex-1 space-y-4 rounded-lg border border-gray-200 p-4 text-sm md:space-y-6 md:px-6 md:py-7 md:text-lg">
              {venue.productivityHighlights.map((highlight) => (
                <div key={highlight.id} className="flex items-start space-x-3 md:space-x-6">
                  <div className="relative inline-flex flex-shrink-0">
                    <IconCheckmarkCircle className="text-gray-900" size="24" />
                    <div className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-primary-600 opacity-30 blur-xl" />
                  </div>
                  <span className="pt-0.5 md:pt-0">{highlight.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <VenueFormFirstStep venue={venue} />
      </div>

      <div className="mt-8 space-y-6 md:mt-20">
        <h4 className="text-2xl font-semibold md:text-3xl">{t('venue.detailsTitle')}</h4>

        <div>
          <Accordion isOpenByDefault headline={t('venue.description.title')}>
            <div className="max-w-3xl md:text-lg">
              <p>{venue.description}</p>
            </div>
          </Accordion>

          <Accordion isOpenByDefault headline={t('venue.detailsAmenities.title')}>
            <div className="space-y-12 md:space-y-16">
              <div className="space-y-4">
                <h4 className="text-xl font-semibold">{t('venue.details.title')}</h4>

                <div className="flex flex-col lg:flex-row lg:space-x-20">
                  <div className="flex-1 divide-y divide-gray-100 text-sm">
                    {venue.venueType && (
                      <div className="flex space-x-5 py-2.5">
                        <div className="w-full max-w-[130px] text-gray-500 md:max-w-[200px]">
                          {t('venue.details.venueType.label')}
                        </div>
                        <div className="flex-1">{venue.venueType.title}</div>
                      </div>
                    )}

                    {!!venue.groupSize && (
                      <div className="flex space-x-5 py-2.5">
                        <div className="w-full max-w-[130px] text-gray-500 md:max-w-[200px]">
                          {t('venue.details.groupSize.label')}
                        </div>
                        <div className="flex-1">
                          {t('venue.details.groupSize.count', { count: venue.groupSize })}
                        </div>
                      </div>
                    )}

                    {roomDetails && (
                      <div className="flex space-x-5 py-2.5">
                        <div className="w-full max-w-[130px] text-gray-500 md:max-w-[200px]">
                          {t('venue.details.rooms.label')}
                        </div>
                        <div className="flex-1"> {roomDetails} </div>
                      </div>
                    )}

                    {!!venue.apartments && (
                      <div className="flex space-x-5 py-2.5">
                        <div className="w-full max-w-[130px] text-gray-500 md:max-w-[200px]">
                          {t('venue.details.apartments.label')}
                        </div>
                        <div className="flex-1">
                          {t('venue.details.apartments.count', { count: venue.apartments })}
                        </div>
                      </div>
                    )}

                    {typeof venue.bathroomsPrivate === 'boolean' && (
                      <div className="flex space-x-5 py-2.5">
                        <div className="w-full max-w-[130px] text-gray-500 md:max-w-[200px]">
                          {t('venue.details.bathrooms.label')}
                        </div>
                        <div className="flex-1">
                          {venue.bathroomsPrivate &&
                            !venue.bathroomsPrivateLimit &&
                            t('venue.details.bathrooms.privateAll')}

                          {venue.bathroomsPrivate &&
                            venue.bathroomsPrivateLimit &&
                            t('venue.details.bathrooms.mixedCount', {
                              count: bathroomsTotal,
                              privateCount: venue.bathroomsPrivateLimit,
                            })}

                          {!venue.bathroomsPrivate &&
                            venue.bathroomsShared &&
                            t('venue.details.bathrooms.count', { count: venue.bathroomsShared })}
                        </div>
                      </div>
                    )}

                    {(venue.area || venue.country) && (
                      <div className="flex space-x-5 py-2.5">
                        <div className="w-full max-w-[130px] text-gray-500 md:max-w-[200px]">
                          {t('venue.details.location.label')}
                        </div>
                        <div className="flex-1">
                          {venue.area?.title}
                          {venue.area && venue.country && <>, </>}
                          {venue.country?.title}
                          {venue.geoCoordinates && (
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${venue.geoCoordinates.latitude},${venue.geoCoordinates.longitude}`}
                              target="_blank"
                              rel="noreferrer"
                              className="ml-1"
                            >
                              ({t('venue.details.location.link')})
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                    {venue.airport && (
                      <div className="flex space-x-5 py-2.5">
                        <div className="w-full max-w-[130px] text-gray-500 md:max-w-[200px]">
                          {t('venue.details.airport.label')}
                        </div>
                        <div className="flex-1">{venue.airport}</div>
                      </div>
                    )}
                    {venue.trainStation && (
                      <div className="flex space-x-5 py-2.5">
                        <div className="w-full max-w-[130px] text-gray-500 md:max-w-[200px]">
                          {t('venue.details.trainStation.label')}
                        </div>
                        <div className="flex-1">{venue.trainStation}</div>
                      </div>
                    )}
                  </div>

                  <div className="w-full pt-8 md:pt-12 lg:max-w-md">
                    <Mapbox
                      lat={venue.geoCoordinates?.latitude}
                      long={venue.geoCoordinates?.longitude}
                    />
                  </div>
                </div>
              </div>

              {venue.amenities?.length > 0 && (
                <div className="space-y-6">
                  <h4 className="text-xl font-semibold">{t('venue.amenities.title')}</h4>

                  <div className="grid grid-cols-2 gap-6 md:grid-cols-3">
                    {venue.amenities.map((amenity) => (
                      <ListItemIcon
                        icon={amenity.icon?.url}
                        label={amenity.title}
                        key={amenity.id}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Accordion>

          {venue.meetingFacilities.length > 0 && (
            <Accordion isOpenByDefault headline={t('venue.facilities.title')}>
              <div
                className={clsx(
                  'grid snap-x auto-cols-max grid-flow-col items-start gap-x-5 overflow-auto pb-6',
                  'md:grid-flow md:grid-flow-row md:grid-cols-2 md:gap-x-8 md:gap-y-10 md:pb-0',
                  'lg:grid-cols-3'
                )}
              >
                {venue.meetingFacilities.map((facility) => (
                  <CardFacilities
                    key={facility.id}
                    className="h-full w-[260px] snap-center md:w-auto"
                    facility={facility}
                  />
                ))}
              </div>
            </Accordion>
          )}

          {venue.cateringOptions.length > 0 && (
            <Accordion headline={t('venue.catering.title')}>
              <div className="max-w-[786px]">
                <PriceList verticalSpacing="md" data={venue.cateringOptions} />
              </div>
            </Accordion>
          )}

          <Accordion headline={t('venue.activities.title')}>
            <div>
              {venue.activities?.length > 0 && (
                <div
                  className={clsx(
                    'grid snap-x auto-cols-max grid-flow-col items-start gap-x-6 overflow-auto pb-6',
                    'md:grid-flow md:grid-flow-row md:grid-cols-2 md:gap-x-8 md:gap-y-10 md:pb-0',
                    'lg:grid-cols-4'
                  )}
                >
                  {venue.activities.map((activity) => (
                    <CardActivity
                      className="h-full w-[260px] snap-center md:w-auto"
                      key={activity.id}
                      href={activity.url}
                      image={activity.thumbnail?.url832_960}
                      headline={activity.title}
                      description={activity.description}
                      duration={activity.duration}
                      persons={activity.groupSize}
                      price={activity.price}
                    />
                  ))}
                </div>
              )}
            </div>
          </Accordion>

          {venue.transportationOptions.length > 0 && (
            <Accordion headline={t('venue.transportation.title')}>
              <div className="max-w-[786px]">
                <PriceList verticalSpacing="md" data={venue.transportationOptions} />
              </div>
            </Accordion>
          )}
        </div>
      </div>

      <ModalGallery closeModal={closeModal} isOpen={openGallery}>
        <div className="mx-auto max-w-5xl space-y-3 md:space-y-5">
          {venue.gallery.map((asset) => (
            <div key={asset.url2048_1360}>
              <Image
                src={asset.url2048_1360}
                width={1024}
                height={680}
                layout="responsive"
                alt={venue.title}
                className="rounded-md"
              />
            </div>
          ))}
        </div>
      </ModalGallery>
    </>
  )
}

export default VenueDetails
